import { type ReactNode, useEffect, useRef, useState } from 'react'

import { usePageScrollLock } from '../hooks/usePageScrollLock'

/**
 * Bottom-sheet primitive — handles mount/unmount with slide-up/down +
 * backdrop fade, Escape-to-close, backdrop tap, and page-scroll lock.
 * Consumers only provide the body content and any per-instance styling
 * via `panelClassName` / `backdropClassName`.
 *
 * Animation contract:
 *   - opens: mount → first paint with translate-y-full → next frame
 *     transitions to translate-y-0 + backdrop fades to full opacity
 *   - closes: backdrop fades + panel translates back to translate-y-full,
 *     DOM unmounts after the animation completes (~220 ms)
 *
 * Two-phase visibility (`mounted` separate from `animateOpen`) is what
 * lets the close animation play before unmount. The double-state read
 * is intentional — using a single boolean would require unmounting on
 * close=true, which kills the slide-out animation.
 *
 * Used by: leaderboard DetailSheet, score SubmitSheet + HighScoreSheet,
 * poker StackEditSheet. Default styling (z-[60], bg-paper, rounded-t-2xl)
 * matches the most common consumer; overrides via the className props
 * cover the rest.
 */
const ANIM_MS = 220

const BottomSheet = ({
  open,
  onClose,
  ariaLabel,
  panelClassName = 'max-w-md',
  backdropClassName = 'bg-black/40',
  showGrabHandle = false,
  swipeToClose = false,
  children,
}: {
  open: boolean
  onClose: () => void
  ariaLabel?: string
  /** Tailwind classes on the sliding panel — sizing/padding/layout. */
  panelClassName?: string
  /** Tailwind classes on the backdrop — default is a soft black/40. */
  backdropClassName?: string
  /** Grab-handle pill at the top of the panel (mobile sheet convention). */
  showGrabHandle?: boolean
  /**
   * Drag the panel down to dismiss.
   *
   * Only opt in on a panel that sets `overscroll-contain`: without it a
   * downward drag at scroll-top reaches the browser and fires pull-to-refresh,
   * which reloads the page mid-round. The drag also only starts when the panel
   * is scrolled to the top, so a swipe inside a scrolled body still scrolls.
   */
  swipeToClose?: boolean
  children: ReactNode
}) => {
  const [mounted, setMounted] = useState(open)
  const [animateOpen, setAnimateOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const dragStartY = useRef<number | null>(null)
  const [dragY, setDragY] = useState(0)

  // Past this the sheet is going away; short of it, it springs back.
  const DISMISS_PX = 90

  const onTouchStart = (e: React.TouchEvent) => {
    if (!swipeToClose) return
    // A scrolled body scrolls; only a sheet already at the top can be dragged.
    if ((panelRef.current?.scrollTop ?? 0) > 0) return
    dragStartY.current = e.touches[0].clientY
  }
  const onTouchMove = (e: React.TouchEvent) => {
    if (dragStartY.current == null) return
    const dy = e.touches[0].clientY - dragStartY.current
    // Upward drags belong to the scroller, not the dismiss gesture.
    setDragY(dy > 0 ? dy : 0)
  }
  const onTouchEnd = () => {
    if (dragStartY.current == null) return
    const shouldClose = dragY > DISMISS_PX
    dragStartY.current = null
    setDragY(0)
    if (shouldClose) onClose()
  }

  // A reopened sheet must not inherit the last drag offset.
  useEffect(() => {
    if (!open) setDragY(0)
  }, [open])

  // Phase 1 — mount on open=true; schedule unmount on open=false after
  // the close animation finishes.
  useEffect(() => {
    if (open) {
      setMounted(true)
      return undefined
    }
    setAnimateOpen(false)
    const t = window.setTimeout(() => setMounted(false), ANIM_MS)
    return () => window.clearTimeout(t)
  }, [open])

  // Phase 2 — once mounted AND open, wait a frame so the initial render
  // pins at translate-y-full, then flip animateOpen so the CSS
  // transition fires. Without the RAF the browser collapses both
  // states into a single paint and the slide-up doesn't happen.
  //
  // The timer is a floor under the RAF, not a second animation: a page the
  // browser has stopped painting (a backgrounded tab, a throttled one) never
  // delivers the frame, and the sheet would sit at translate-y-full behind
  // its own backdrop — invisible, but with the body scroll locked and taps
  // landing on the backdrop. Whichever fires first flips the same flag.
  useEffect(() => {
    if (!mounted || !open) return undefined
    const raf = window.requestAnimationFrame(() => setAnimateOpen(true))
    const floor = window.setTimeout(() => setAnimateOpen(true), 50)
    return () => {
      window.cancelAnimationFrame(raf)
      window.clearTimeout(floor)
    }
  }, [mounted, open])

  // Escape-to-close + page scroll lock — active for the entire mounted
  // window so users can dismiss even mid-animation.
  usePageScrollLock(mounted)
  useEffect(() => {
    if (!mounted) return undefined
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [mounted, onClose])

  if (!mounted) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center">
      <button
        type="button"
        aria-label="Lukk"
        onClick={onClose}
        className={`absolute inset-0 cursor-default transition-opacity duration-200 ${backdropClassName} ${
          animateOpen ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        // While dragging, the finger owns the position — so no transition,
        // and the inline transform overrides the open/closed class.
        style={
          dragY > 0
            ? { transform: `translateY(${dragY}px)`, transition: 'none' }
            : undefined
        }
        // `max-h-[90dvh]` is the default cap so a long body doesn't
        // push past the top of the screen. Consumers that need
        // internal scrolling (DetailSheet) ALSO set the panel itself
        // to `overflow-y-auto` and put a sticky header inside —
        // single-layer scroll is the iOS-Safari-friendly recipe.
        //
        // No default `bg-*` — the consumer picks the canvas. App
        // language puts cards (bg-paper) on a cream canvas
        // (bg-cream-bg) and the leaderboard detail sheet wants the
        // latter; the simple sheets (StackEditSheet, SubmitSheet,
        // HighScoreSheet) want the former.
        className={`relative w-full rounded-t-2xl shadow-xl max-h-[90dvh] transition-transform duration-200 ease-out ${
          animateOpen ? 'translate-y-0' : 'translate-y-full'
        } ${panelClassName}`}
      >
        {showGrabHandle ? (
          <div className="flex justify-center pt-3">
            <span
              aria-hidden
              className="block w-10 h-1.5 rounded-full bg-ink-200"
            />
          </div>
        ) : null}
        {children}
      </div>
    </div>
  )
}

export default BottomSheet
